import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import z from "zod";

import { withJWTClient } from "./connectClient";

import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

const API_BASE_URL: string = import.meta.env.DEV
  ? "/recognition"
  : (import.meta.env.VITE_RECOGNITION_API_URL as string) || "";

export const addressFormatSchema = z.object({
  countryCode: z.string(),
  regionCode: z.string(),
  city: z.string(),
  address1: z.string(),
  address2: z.string().optional(),
  postalCode: z.string(),
  phone: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  ipAddress: z.string(),
});

export type addressFormat = z.infer<typeof addressFormatSchema>;

export const recognitionUserSchema = z.object({
  id: z.string(),
  websiteId: z.string(),
  email: z.string(),
  phone: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  customer: z.boolean(),
  createdAt: z.string(),
  location: z.string(),
  hardBounced: z.boolean(),
  hardBouncedUpdatedAt: z.string(),
  complained: z.boolean(),
  complainedUpdatedAt: z.string(),
  userProvidedAddress: addressFormatSchema.nullable(),
});

export type RecognitionUser = z.infer<typeof recognitionUserSchema>;

export type ConsentState =
  | "CONSENT_STATE_SUBSCRIBED"
  | "CONSENT_STATE_UNSUBSCRIBED"
  | "CONSENT_STATE_UNSPECIFIED";

export type UserConsent = {
  state: ConsentState;
  source: string;
  updatedAt: string | null;
};

export const birthDateSchema = z.object({
  day: z.number(),
  month: z.number(),
  year: z.number().nullable(),
});

export type BirthDate = z.infer<typeof birthDateSchema>;

export type SuppressedState =
  | "SUPPRESSED_STATE_SUPPRESSED"
  | "SUPPRESSED_STATE_UNSUPPRESSED"
  | "SUPPRESSED_STATE_UNSPECIFIED";

// Do-not-send state, tracked apart from consent so a re-subscribe can't clear
// it. Mirrors Suppressed in recognition_service.proto, which carries no reason.
// This is the manual suppression; hard bounces and complaints suppress too and
// are carried separately on the user (see UserHardBounce / UserComplaint).
export type UserSuppression = {
  state: SuppressedState;
  source: string;
  updatedAt: string | null;
};

// `state` is optional because proto3 omits false booleans from JSON.
export type UserHardBounce = {
  state?: boolean;
  reason?: string;
  updatedAt: string | null;
};

export type UserComplaint = {
  state?: boolean;
  updatedAt: string | null;
};

// Type for single user endpoint response (matches API spec)
export type RecognitionUserDetail = {
  id: string;
  websiteId: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  createdAt: string; // ISO 8601 date-time format
  emailEligible: boolean;
  smsEligible: boolean;
  emailConsent?: UserConsent;
  smsConsent?: UserConsent;
  emailSuppressed?: UserSuppression;
  hardBounced?: UserHardBounce;
  complained?: UserComplaint;
  userProvidedAddress: addressFormat | null;
  networkDerivedAddress: addressFormat | null;
  tags: string[];
  etag: string;
  metadata?: Record<string, string>;
  birthDate: BirthDate | null;
};

// The address UpdateUser accepts; the whole object is sent on every change.
// address1, city and countryCode (ISO 3166-1 alpha-2) are required.
export type UserProvidedAddressInput = {
  address1: string;
  address2: string;
  city: string;
  regionCode: string;
  countryCode: string;
  postalCode: string;
};

// firstName and lastName must be sent together; an empty string clears one.
export type UserDetailsUpdate = {
  firstName?: string;
  lastName?: string;
  userProvidedAddress?: UserProvidedAddressInput;
};

export type PatchRecognitionUserBody = UserDetailsUpdate & {
  etag: string;
  emailConsent?: {
    state: ConsentState;
    source: string;
  };
  smsConsent?: {
    state: ConsentState;
    source: string;
  };
  // `updatedAt` is deliberately absent: UpdateUser ignores it and stamps the
  // server clock instead.
  emailSuppressed?: {
    state: SuppressedState;
    source: string;
  };
};

// Subject Access Request (SAR) export of a single user's profile.
// The endpoint returns the current export state; `downloadUrl` is only
// populated once the export has finished processing.
export const sarExportSchema = z.object({
  exportId: z.string(),
  status: z.string(),
  downloadUrl: z.string().optional().default(""),
});

export type SarExport = z.infer<typeof sarExportSchema>;

// Type for user events response
export type RecognitionEventEncoded = {
  type: string;
  data: object;
  timestamp: string; // ISO 8601 date-time format
};

// Query parameters for user events endpoint
export type GetUserEventsParams = {
  next?: string; // Pagination token from previous response
  since?: string; // RFC 3339 date-time, e.g. "2026-01-01T12:34:56Z"
  eventTypes?: string[]; // Array of event types to filter by
  limit?: number; // Max number of events (default: 100, max: 500)
  json?: string; // When present, returns JSON instead of base64-encoded protobuf
};

// Paginated user events response (API may always return "next"; stop when empty or no new events)
export type RecognitionUserEventsResponse = {
  events: RecognitionEventEncoded[];
  next?: string;
};

export interface RecognitionUsersResponse {
  users: RecognitionUser[];
  totalUsers: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface GetRecognitionUsersParams {
  page?: number;
  pageSize?: number;
  sort?: string;
  q?: string;
  userIds?: string[];
}

export const getRecognitionUsers = async (
  siteId: string,
  params?: GetRecognitionUsersParams,
): Promise<RecognitionUsersResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.page !== undefined) {
    queryParams.append("page", params.page.toString());
  }
  if (params?.pageSize !== undefined) {
    queryParams.append("pageSize", params.pageSize.toString());
  }
  if (params?.sort) {
    queryParams.append("sort", params.sort);
  }
  if (params?.q) {
    queryParams.append("q", params.q);
  }
  if (params?.userIds && params.userIds.length > 0) {
    params.userIds.forEach((userId) => {
      queryParams.append("userIds", userId);
    });
  }

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/websites/${siteId}/users${queryString ? `?${queryString}` : ""}`;

  const response = await withJWTClient((client) => client.get(url));
  return response.data as RecognitionUsersResponse;
};

function parseRecognitionUserDetail(data: unknown): RecognitionUserDetail {
  const user = data as RecognitionUserDetail;
  return {
    ...user,
    birthDate: birthDateSchema.nullable().parse(user.birthDate ?? null),
  };
}

/**
 * Get a single recognition user by website ID and user ID
 * @param websiteId - The website ID
 * @param userId - The user ID
 * @returns Promise resolving to the recognition user details
 */
export const getRecognitionUser = async (
  websiteId: string,
  userId: string,
): Promise<RecognitionUserDetail> => {
  const response = await withJWTClient((client) =>
    client.get(`${API_BASE_URL}/websites/${websiteId}/users/${userId}`),
  );
  return parseRecognitionUserDetail(response.data);
};

export const deleteRecognitionUser = async (
  websiteId: string,
  userId: string,
): Promise<void> => {
  await withJWTClient((client) =>
    client.delete(`${API_BASE_URL}/websites/${websiteId}/users/${userId}`),
  );
};

export const patchRecognitionUser = async (
  websiteId: string,
  userId: string,
  body: PatchRecognitionUserBody,
): Promise<RecognitionUserDetail> => {
  const response = await withJWTClient((client) =>
    client.patch(`${API_BASE_URL}/websites/${websiteId}/users/${userId}`, body),
  );
  return parseRecognitionUserDetail(response.data);
};

/**
 * Fetch the state of a user's Subject Access Request (SAR) export.
 *
 * The endpoint is GET-only: calling it without an `exportId` starts a new
 * export and returns its `exportId`; passing that `exportId` back polls the
 * existing export until a `downloadUrl` is available (complete) or it fails.
 */
export const getSarExport = async (
  websiteId: string,
  userId: string,
  exportId?: string,
): Promise<SarExport> => {
  const query = exportId
    ? `?${new URLSearchParams({ exportId }).toString()}`
    : "";
  const response = await withJWTClient((client) =>
    client.get(
      `${API_BASE_URL}/websites/${websiteId}/users/${userId}/sar${query}`,
    ),
  );
  return sarExportSchema.parse(response.data);
};

/**
 * Get events for a recognition user (paginated; API may always return "next").
 * @returns Promise resolving to { events, next? }
 */
export const getRecognitionUserEvents = async (
  websiteId: string,
  userId: string,
  params?: GetUserEventsParams,
): Promise<RecognitionUserEventsResponse> => {
  const searchParams = new URLSearchParams();

  if (params?.next) {
    searchParams.append("next", params.next);
  }
  if (params?.since) {
    searchParams.append("since", params.since);
  }
  if (params?.eventTypes && params.eventTypes.length > 0) {
    // Wire name is snake_case and repeated, per recognition_service.proto:
    // ?event_types=click&event_types=send
    params.eventTypes.forEach((eventType) => {
      searchParams.append("event_types", eventType);
    });
  }
  if (params?.limit !== undefined) {
    searchParams.append("limit", params.limit.toString());
  }

  searchParams.append("json", "true"); // default to JSON

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/websites/${websiteId}/users/${userId}/events${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await withJWTClient((client) => client.get(url));
  const data = response.data as
    | RecognitionUserEventsResponse
    | RecognitionEventEncoded[];
  if (Array.isArray(data)) {
    return { events: data };
  }
  return data;
};

// Query keys
export const recognitionUsersKeys = {
  all: (siteId: string) => [siteId, "recognitionUsers"],
  detail: (websiteId: string, userId: string) => [
    "recognitionUser",
    websiteId,
    userId,
  ],
  events: (websiteId: string, userId: string, params?: GetUserEventsParams) => [
    "recognitionUserEvents",
    websiteId,
    userId,
    params,
  ],
  list: (siteId: string, params?: GetRecognitionUsersParams) => [
    ...recognitionUsersKeys.all(siteId),
    "list",
    params,
  ],
  eventsInfinite: (
    websiteId: string,
    userId: string,
    params?: Omit<GetUserEventsParams, "next">,
  ) => ["recognitionUserEventsInfinite", websiteId, userId, params],
  sar: (websiteId: string, userId: string, exportId: string | null) => [
    "recognitionUserSar",
    websiteId,
    userId,
    exportId,
  ],
};

export const recognitionUsersQueryOptions = (
  siteId: string,
  params?: GetRecognitionUsersParams,
) => ({
  queryKey: recognitionUsersKeys.list(siteId, params),
  queryFn: () => getRecognitionUsers(siteId, params),
  enabled: !!siteId,
});

// React Query hook
export function useRecognitionUsers(
  siteId: string,
  params?: GetRecognitionUsersParams,
  options?: UseQueryOptions<RecognitionUsersResponse>,
) {
  return useQuery({
    ...recognitionUsersQueryOptions(siteId, params),
    ...options,
  });
}

/**
 * React Query hook to get a single recognition user
 * @param websiteId - The website ID
 * @param userId - The user ID
 * @param options - Optional React Query options
 * @returns Query result with recognition user details
 */
export function useRecognitionUser(
  websiteId: string,
  userId: string,
  options?: UseQueryOptions<RecognitionUserDetail>,
) {
  return useQuery({
    queryKey: recognitionUsersKeys.detail(websiteId, userId),
    queryFn: () => getRecognitionUser(websiteId, userId),
    enabled: !!websiteId && !!userId,
    ...options,
  });
}

/**
 * React Query hook to get events for a recognition user
 * @returns Query result with { events, next? }
 */
export function useRecognitionUserEvents(
  websiteId: string,
  userId: string,
  params?: GetUserEventsParams,
  options?: UseQueryOptions<RecognitionUserEventsResponse>,
) {
  return useQuery({
    queryKey: recognitionUsersKeys.events(websiteId, userId, params),
    queryFn: () => getRecognitionUserEvents(websiteId, userId, params),
    enabled: !!websiteId && !!userId,
    ...options,
  });
}

export function useDeleteRecognitionUser() {
  return useMutation({
    mutationFn: ({
      websiteId,
      userId,
    }: {
      websiteId: string;
      userId: string;
    }) => deleteRecognitionUser(websiteId, userId),
  });
}

/**
 * Save name/address edits from the Profile Details tab. On a 409 (stale etag),
 * re-fetch once and resubmit: last-write-wins for the sections being saved.
 */
export function useUpdateRecognitionUserDetails(
  websiteId: string,
  userId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      etag,
      update,
    }: {
      etag: string;
      update: UserDetailsUpdate;
    }) => {
      const detailKey = recognitionUsersKeys.detail(websiteId, userId);
      const latestEtag =
        queryClient.getQueryData<RecognitionUserDetail>(detailKey)?.etag ??
        etag;
      try {
        return await patchRecognitionUser(websiteId, userId, {
          etag: latestEtag,
          ...update,
        });
      } catch (error) {
        if (!isAxiosError(error) || error.response?.status !== 409) throw error;
        const fresh = await getRecognitionUser(websiteId, userId);
        return patchRecognitionUser(websiteId, userId, {
          etag: fresh.etag,
          ...update,
        });
      }
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(
        recognitionUsersKeys.detail(websiteId, userId),
        updatedUser,
      );
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to save profile details");
    },
  });
}

/** The export is ready to download once a `downloadUrl` is present. */
export const isSarExportComplete = (data: SarExport): boolean =>
  data.downloadUrl.length > 0;

/** Treat any status containing "FAIL" as a terminal failure. */
export const isSarExportFailed = (data: SarExport): boolean =>
  data.status.toUpperCase().includes("FAIL");

/**
 * Mutation to kick off a SAR profile export.
 *
 * Mirrors the list export flow: the initial GET (no `exportId`) starts the
 * export and returns its `exportId`, which is then handed to
 * `useSarExportStatus` to poll until a download URL is available.
 */
export function useExportUserProfile() {
  return useMutation({
    mutationFn: ({
      websiteId,
      userId,
    }: {
      websiteId: string;
      userId: string;
    }) => getSarExport(websiteId, userId),
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to export profile");
    },
  });
}

// SAR export polling cadence and safety caps. The export is asynchronous, so
// we poll on an interval and stop after a bounded number of attempts so a
// stuck export can never poll indefinitely for the component's lifetime.
export const SAR_POLL_INTERVAL_MS = 5000;
export const SAR_MAX_POLL_ATTEMPTS = 72; // ~6 minutes at 5s intervals
export const SAR_EXPORT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Poll the SAR export status every 5s while an `exportId` is set, stopping
 * once the export is complete (has a download URL), has failed, or the
 * maximum number of poll attempts is reached.
 */
export function useSarExportStatus(
  websiteId: string,
  userId: string,
  exportId: string | null,
) {
  return useQuery<SarExport>({
    queryKey: recognitionUsersKeys.sar(websiteId, userId, exportId),
    queryFn: () => getSarExport(websiteId, userId, exportId ?? undefined),
    enabled: !!websiteId && !!userId && !!exportId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return SAR_POLL_INTERVAL_MS;
      if (isSarExportComplete(data) || isSarExportFailed(data)) return false;
      // Safety net: stop polling after the cap even if the backend never
      // reaches a terminal state.
      if (query.state.dataUpdateCount >= SAR_MAX_POLL_ATTEMPTS) return false;
      return SAR_POLL_INTERVAL_MS;
    },
  });
}

/**
 * Infinite query for user events. API may always return "next"; we stop when:
 * - last page is empty (events.length === 0), or
 * - last page added no new events after deduping (handled in UI).
 */
export function useRecognitionUserEventsInfinite(
  websiteId: string,
  userId: string,
  params?: Omit<GetUserEventsParams, "next">,
  options?: { enabled?: boolean; refetchInterval?: number },
): UseInfiniteQueryResult<InfiniteData<RecognitionUserEventsResponse>> {
  return useInfiniteQuery({
    queryKey: recognitionUsersKeys.eventsInfinite(websiteId, userId, params),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      getRecognitionUserEvents(websiteId, userId, {
        ...params,
        ...(pageParam ? { next: pageParam } : {}),
      }),
    getNextPageParam: (lastPage) =>
      lastPage.events.length === 0 ? undefined : lastPage.next,
    initialPageParam: undefined as string | undefined,
    enabled: !!websiteId && !!userId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
  });
}
